import { api } from './http';
import { API_ENDPOINTS } from '@/config/constants';

// Types based on Swagger documentation
export interface WhitelistFromApi {
  id: number;
  user_id: string;
  filename: string;
  size: number;
  content_type: string;
  file_url: string; // deprecated - will be removed
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  last_version_id: number; // ID of the latest version for download
}

export interface WhitelistVersionFromApi {
  id: number;
  filename: string;
  size: number;
  content_type: string;
  file_url: string; // deprecated - will be removed
  created_at: string;
  created_by: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
}

export interface WhitelistsResponse {
  data: WhitelistFromApi[];
  pagination: {
    current_page: number;
    next_page: number | null;
    prev_page: number | null;
    total_pages: number;
    total_count: number;
    per_page: number;
  };
}

export interface WhitelistVersionsResponse {
  data: WhitelistVersionFromApi[];
}

export interface WhitelistsFilters {
  page?: number;
  per_page?: number;
  order?: 'title' | 'created_at' | 'updated_at';
  order_direction?: 'asc' | 'desc';
}

// API functions
export async function getWhitelists(
  filters?: WhitelistsFilters
): Promise<WhitelistsResponse> {
  const params = new URLSearchParams();

  if (filters?.page) {
    params.append('page', filters.page.toString());
  }

  if (filters?.per_page) {
    params.append('per_page', filters.per_page.toString());
  }

  if (filters?.order) {
    params.append('order', filters.order);
  }

  if (filters?.order_direction) {
    params.append('order_direction', filters.order_direction);
  }

  const url = `${API_ENDPOINTS.WHITELISTS}${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await api.get(url);

  if (
    response.data &&
    response.data.data &&
    Array.isArray(response.data.data) &&
    response.data.pagination
  ) {
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  }

  // Fallback for unexpected response structure
  return {
    data: [],
    pagination: {
      current_page: 1,
      next_page: null,
      prev_page: null,
      total_pages: 0,
      total_count: 0,
      per_page: 25,
    },
  };
}

export async function uploadWhitelist(file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);

  await api.post(API_ENDPOINTS.WHITELISTS, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export async function updateWhitelist(id: number, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);

  await api.put(`${API_ENDPOINTS.WHITELISTS}/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export async function deleteWhitelist(id: number): Promise<void> {
  await api.delete(`${API_ENDPOINTS.WHITELISTS}/${id}`);
}

export async function getWhitelistVersions(
  whitelistId: number
): Promise<WhitelistVersionsResponse> {
  const params = new URLSearchParams();
  params.append('whitelist_id', whitelistId.toString());

  const url = `${API_ENDPOINTS.WHITELISTS_VERSIONS}?${params.toString()}`;
  const response = await api.get(url);

  if (
    response.data &&
    response.data.data &&
    Array.isArray(response.data.data)
  ) {
    return {
      data: response.data.data,
    };
  }

  return {
    data: [],
  };
}

// Alternative download method using window.open
async function downloadWhitelistFallback(
  fileUrl: string
): Promise<void> {
  // Try opening in new window/tab for download
  const downloadWindow = window.open(fileUrl, '_blank');

  if (!downloadWindow) {
    throw new Error('Popup blocked - unable to download file');
  }

  // Close the window after a short delay
  setTimeout(() => {
    downloadWindow.close();
  }, 2000);
}

export async function downloadWhitelist(
  fileUrl: string,
  filename: string
): Promise<void> {
  // Check if fileUrl is absolute or relative
  let downloadUrl = fileUrl;
  if (!fileUrl.startsWith('http')) {
    // If relative URL, we need to handle it properly
    if (fileUrl.startsWith('/')) {
      // Absolute path - use as is with API base
      downloadUrl = fileUrl;
    } else {
      // Relative path - prefix with API base
      downloadUrl = `/api/${fileUrl}`;
    }
  }

  try {
    const response = await api.get(downloadUrl, {
      responseType: 'blob',
      timeout: 30000, // 30 second timeout
    });

    // Check if response is actually a blob and not empty
    if (!response.data || response.data.size === 0) {
      await downloadWhitelistFallback(downloadUrl);
      return;
    }

    const blob = new Blob([response.data]);
    const link = document.createElement('a');

    // Check if browser supports download attribute
    if ('download' in link) {
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up after a short delay to ensure download starts
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    } else {
      await downloadWhitelistFallback(downloadUrl);
    }
  } catch {
    await downloadWhitelistFallback(downloadUrl);
  }
}
